import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from './utils';

/**
 * Checkbox — custom-rendered checkbox aligned to the neutral-inverse
 * grammar shared by PillButton primary, FilterChipGroup selected, etc.
 *
 * Visual:
 *   unchecked  bg-transparent + border-foreground/20 + rounded-xs (4px → 25%
 *              radius su 16px = quadrato distinto, non cerchio)
 *   checked    bg-foreground + border-foreground + Check icon overlay
 *              in contrasting text-background color (black-on-white in
 *              light, white-on-black in dark — same rule as the selected
 *              PillButton primary)
 *   focus      subtle outline-foreground/30 on keyboard focus only
 *   disabled   opacity-50, no pointer events
 *
 * Implementation:
 *   - native <input type="checkbox"> remains for a11y (role, keyboard
 *     space-toggle, form semantics). `appearance: none` hides the UA
 *     visual so we can draw our own.
 *   - a sibling <Check /> icon is absolutely positioned and appears via
 *     `peer-checked:opacity-100`. Pure-CSS, no state or ref juggling.
 *
 * Consumer contract:
 *   className is forwarded to the outer wrapper (size overrides like
 *   `h-5 w-5` are honored). Do NOT pass border / bg / accent / rounded
 *   from call sites — those are owned by the primitive.
 */
export type CheckboxProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type'
>;

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, disabled, ...props }, ref) => (
    <span
      className={cn(
        'relative inline-flex h-4 w-4 shrink-0 align-middle',
        disabled && 'opacity-50',
        className,
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        className={cn(
          'peer absolute inset-0 h-full w-full cursor-pointer appearance-none rounded-xs border border-foreground/20 bg-transparent transition-colors',
          'checked:border-foreground checked:bg-foreground',
          'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground/30',
          'disabled:cursor-not-allowed',
        )}
        {...props}
      />
      <Check
        aria-hidden
        strokeWidth={3}
        className="pointer-events-none absolute inset-0 h-full w-full p-0.5 text-background opacity-0 transition-opacity peer-checked:opacity-100"
      />
    </span>
  ),
);
Checkbox.displayName = 'Checkbox';
